import React, { useState, useCallback } from "react";
import {
    Layout, Table, Input, Button, Row, Col,
    Breadcrumb, message, Select, Tooltip, DatePicker, Empty
} from "antd";
import {
    HomeOutlined, BarChartOutlined, SearchOutlined, FilterOutlined, ClearOutlined,
    PrinterOutlined, FileExcelOutlined
} from "@ant-design/icons";
import Headerdiv from "../../pages/layout/header";
import Footerdiv from "../../pages/layout/footer";
import api from "../../services/axiosInstance";
import dayjs from "dayjs";
import ReportPreviewModal from "./ReportPreviewModal";
import { buildReportPDF } from "../../utils/reportPdfBuilder";
import { exportRowsToExcel } from "../../utils/reportExport";
import "../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const CARD_COLORS = { green: "#2d5a3d", red: "#cf1322", orange: "#d48806", blue: "#1677ff" };

/**
 * Config-driven report page. All report pages (inventory, maintenance,
 * expenses, farm) share this component so filters, print (PDF preview +
 * download) and Excel export behave identically everywhere.
 *
 * Props:
 * @param {string} title        - Page title
 * @param {string} subtitle     - Page subtitle
 * @param {string[]} breadcrumb - Breadcrumb items after Home (e.g. ["Reports", "Inventory"])
 * @param {string} fetchUrl     - Report API URL (GET)
 * @param {Function} [buildParams] - (appliedFilters) => query params (default: built from filterDefs)
 * @param {Array}  filterDefs   - Filter field descriptors (see below)
 * @param {Array}  columns      - antd Table columns
 * @param {string} rowKey       - Table row key field
 * @param {Function} [tableSummary] - (data, apiData) => antd summary node (totals row)
 * @param {Array}  [summaryCards]   - [{ label, value, color }] cards shown above the table
 * @param {Object} pdf          - { title, subtitle, columns, rows, totalRow, summary, columnStyles, orientation }
 * @param {Object} excel        - { filename, sheetName, columns, rows, extraRows }
 * @param {Function} [onData]   - (dataset, apiData) => void (derive dynamic options etc.)
 *
 * Filter defs:
 *  - { type: "search", key, label, placeholder?, col }
 *  - { type: "select", key, label, col, options: [{value,label}], placeholder?, showSearch? }
 *  - { type: "date",   key, label, col, startParam?, endParam? }
 */
const GenericReportPage = ({
    title,
    subtitle = "",
    breadcrumb = [],
    fetchUrl,
    buildParams,
    filterDefs = [],
    columns,
    rowKey,
    tableSummary,
    summaryCards,
    pdf,
    excel,
    onData,
}) => {
    const [data, setData] = useState([]);
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [applied, setApplied] = useState({});
    // Reports are generated on demand: the page shows no data until the user
    // applies a filter, so nothing is fetched/generated automatically.
    const [hasSearched, setHasSearched] = useState(false);

    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [includeFilters, setIncludeFilters] = useState(false);

    const defaultBuildParams = (appliedFilters) => {
        const params = {};
        filterDefs.forEach((def) => {
            const value = appliedFilters[def.key];
            if (def.type === "search") {
                params.search = String(value || "").trim();
            } else if (def.type === "select") {
                params[def.key] = value === undefined || value === null ? "" : value;
            } else if (def.type === "date") {
                params[def.startParam || "start_date"] = value?.[0] ? value[0].format("YYYY-MM-DD") : "";
                params[def.endParam || "end_date"] = value?.[1] ? value[1].format("YYYY-MM-DD") : "";
            }
        });
        return params;
    };

    const fetchData = useCallback(async (appliedFilters) => {
        try {
            setLoading(true);
            const params = (buildParams || defaultBuildParams)(appliedFilters || {});
            const res = await api.get(fetchUrl, { params });
            const dataset = res.data?.dataset || [];
            setData(dataset);
            setApiData(res.data);
            onData?.(dataset, res.data);
        } catch {
            message.error("Failed to fetch report data.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchUrl]);

    // True when the user has actually set at least one filter value.
    const hasFilterValues = (values) =>
        filterDefs.some((def) => {
            const value = values[def.key];
            if (def.type === "search") return String(value || "").trim() !== "";
            if (def.type === "date") return Array.isArray(value) ? value.some(Boolean) : Boolean(value);
            return value !== undefined && value !== null && value !== "";
        });

    const handleFilter = () => {
        // A report is generated only when a filter has been set and applied.
        if (!hasFilterValues(filters)) {
            message.warning("Set at least one filter before applying.");
            return;
        }
        setApplied(filters);
        setHasSearched(true);
        fetchData(filters);
    };

    const handleClear = () => {
        const cleared = {};
        filterDefs.forEach((def) => { cleared[def.key] = undefined; });
        setFilters(cleared);
        setApplied({});
        // Clearing returns to the pre-search state: no data until Apply again.
        setHasSearched(false);
        setData([]);
        setApiData(null);
    };

    const buildFilterLabels = () => {
        const labels = {};
        filterDefs.forEach((def) => {
            const value = applied[def.key];
            if (def.type === "search" && value) {
                labels[def.label] = String(value).trim();
            } else if (def.type === "select" && value !== undefined && value !== null && value !== "") {
                const opt = (def.options || []).find((o) => String(o.value) === String(value));
                labels[def.label] = opt ? opt.label : String(value);
            } else if (def.type === "date" && value?.[0] && value?.[1]) {
                labels[def.label] = `${value[0].format("YYYY-MM-DD")} to ${value[1].format("YYYY-MM-DD")}`;
            }
        });
        return labels;
    };

    const buildDoc = async (includeFiltersValue) =>
        buildReportPDF({
            title: pdf.title,
            subtitle: pdf.subtitle || subtitle,
            columns: pdf.columns || [],
            rows: pdf.rows ? pdf.rows(data, apiData) : [],
            includeFilters: includeFiltersValue,
            filters: buildFilterLabels(),
            summary: pdf.summary ? pdf.summary(data, apiData) : [],
            totalRow: pdf.totalRow ? pdf.totalRow(data, apiData) : null,
            columnStyles: pdf.columnStyles || {},
            orientation: pdf.orientation || "landscape",
        });

    const handlePrint = async () => {
        if (data.length === 0) {
            message.warning("No data to print.");
            return;
        }
        setPrintVisible(true);
        setPdfLoading(true);
        setPdfDataUri(null);
        try {
            const doc = await buildDoc(includeFilters);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleIncludeFiltersChange = async (checked) => {
        setIncludeFilters(checked);
        setPdfLoading(true);
        setPdfDataUri(null);
        try {
            const doc = await buildDoc(checked);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const doc = await buildDoc(includeFilters);
            doc.save(`${excel.filename}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const handleExportExcel = () => {
        if (data.length === 0) {
            message.warning("No data to export.");
            return;
        }
        exportRowsToExcel({
            filename: excel.filename,
            sheetName: excel.sheetName || title,
            columns: excel.columns || [],
            rows: excel.rows ? excel.rows(data, apiData) : data,
            extraRows: excel.extraRows ? excel.extraRows(data, apiData) : [],
        });
        message.success("Excel exported successfully.");
    };

    const hasActiveFilters = Object.values(applied).some((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== ""
    );

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                ...breadcrumb.map((b) => ({ title: b })),
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <BarChartOutlined /> {title}
                        </h1>
                        {subtitle && <p className="fluid-page-subtitle">{subtitle}</p>}
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Tooltip title="Print report">
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={handlePrint}
                                    className="hero-action-btn"
                                >
                                    Print
                                </Button>
                            </Tooltip>
                            <Tooltip title="Export to Excel">
                                <Button
                                    icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                    onClick={handleExportExcel}
                                    className="hero-action-btn"
                                >
                                    Export Excel
                                </Button>
                            </Tooltip>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    {filterDefs.length > 0 && (
                        <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                            <Row gutter={16} align="bottom" className="fluid-filter-row">
                                {filterDefs.map((def) => {
                                    if (def.type === "search") {
                                        return (
                                            <Col xs={24} sm={12} lg={def.col || 5} key={def.key}>
                                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>{def.label}:</div>
                                                <Input
                                                    value={filters[def.key] || ""}
                                                    onChange={(e) => setFilters({ ...filters, [def.key]: e.target.value })}
                                                    onPressEnter={handleFilter}
                                                    placeholder={def.placeholder || "Search..."}
                                                    prefix={<SearchOutlined />}
                                                    allowClear
                                                />
                                            </Col>
                                        );
                                    }
                                    if (def.type === "select") {
                                        return (
                                            <Col xs={24} sm={12} lg={def.col || 4} key={def.key}>
                                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>{def.label}:</div>
                                                <Select
                                                    style={{ width: "100%" }}
                                                    value={filters[def.key]}
                                                    onChange={(v) => setFilters({ ...filters, [def.key]: v })}
                                                    placeholder={def.placeholder || "All"}
                                                    allowClear
                                                    showSearch={def.showSearch}
                                                    optionFilterProp="children"
                                                >
                                                    {(def.options || []).map((opt) => (
                                                        <Option key={String(opt.value)} value={opt.value}>{opt.label}</Option>
                                                    ))}
                                                </Select>
                                            </Col>
                                        );
                                    }
                                    if (def.type === "date") {
                                        return (
                                            <Col xs={24} sm={12} lg={def.col || 5} key={def.key}>
                                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>{def.label}:</div>
                                                <DatePicker.RangePicker
                                                    style={{ width: "100%" }}
                                                    value={filters[def.key]}
                                                    onChange={(dates) => setFilters({ ...filters, [def.key]: dates })}
                                                    placeholder={["Start date", "End date"]}
                                                    allowClear
                                                />
                                            </Col>
                                        );
                                    }
                                    return null;
                                })}
                                <Col xs={24} sm={24} lg={6}>
                                    <div className="fluid-filter-compact-actions">
                                        <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                            Apply
                                        </Button>
                                        <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                            Clear
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {!hasSearched ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Set at least one filter and click Apply to generate the report."
                            style={{ padding: "48px 0" }}
                        />
                    ) : (
                        <>
                    {summaryCards && summaryCards.length > 0 && (
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            {summaryCards.map((card) => (
                                <Col xs={24} sm={12} lg={6} key={card.label}>
                                    <div
                                        style={{
                                            border: "1px solid #e8e8e8",
                                            borderRadius: 8,
                                            padding: "12px 16px",
                                            background: "#fafafa",
                                            marginBottom: 12,
                                        }}
                                    >
                                        <div style={{ fontSize: 12, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                            {card.label}
                                        </div>
                                        <div style={{ fontSize: 20, fontWeight: 600, color: CARD_COLORS[card.color] || "#2d5a3d" }}>
                                            {card.value}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={data}
                            rowKey={rowKey}
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            scroll={{ x: 1100 }}
                            summary={tableSummary ? (pageData) => tableSummary(pageData, apiData) : undefined}
                        />
                    </div>
                        </>
                    )}
                </div>
            </Content>

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title={`${title}${hasActiveFilters ? " (Filtered)" : ""}`}
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                includeFilters={includeFilters}
                onIncludeFiltersChange={handleIncludeFiltersChange}
                onDownload={handleDownloadPDF}
            />

            <Footerdiv />
        </Layout>
    );
};

export default GenericReportPage;
